import { useCallback, useEffect, useState } from 'react'
import { Button } from '@heroui/react'
import { ApiError } from '../../lib/api'
import { problemsNamed, readQueueScreen, screenFrom, type BasicQueue, type BasicView, type QueueScreen } from '../../lib/queueConfig'
import { notLiveText } from '../../lib/branchConfig'
import { explain } from '../../lib/staffErrors'
import { hasPhrase, type StaffLanguage, type Vars } from '../../lib/staffText'
import type { ProfileProblem } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { SETUP_CHANGED } from '../setupChanged'
import { Notice } from '../settingsKit'
import { AskBar } from '../setupKit'

type Say = ReturnType<typeof useStaffText>['say']

/** The Basic screen's data, read again after every write. */
export function useQueueScreen(organisation?: string) {
  const { lang } = useStaffText()
  const [screen, setScreen] = useState<QueueScreen | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setScreen(await readQueueScreen(organisation))
      setError('')
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      setScreen((s) => s ?? { mode: null, editable: false, revision: 0, setups: [], problems: [], services: null })
    }
  }, [organisation, lang])

  useEffect(() => {
    void load()
  }, [load])

  /** A write's answer shown at once and announced; a stale refusal re-reads, so Save again can succeed. */
  const write = useCallback(
    async (call: Promise<BasicView>) => {
      let view: BasicView
      try {
        view = await call
      } catch (err) {
        if (err instanceof ApiError && err.code === 'PROFILE_STALE') void load()
        throw err
      }
      setScreen(await screenFrom(view, organisation))
      setError(notLiveText(view.notLive, lang))
      window.dispatchEvent(new CustomEvent(SETUP_CHANGED))
    },
    [organisation, load, lang],
  )

  return { screen, error, write }
}

/** One problem in our words when we have them (the editor's own as a fallback), else the server's. */
function problemText(p: ProfileProblem, say: Say, prefix: string): string {
  for (const key of [prefix + p.code, 'profiles.problem.' + p.code]) {
    if (hasPhrase(key)) return say(key, p.args as Vars | undefined)
  }
  return p.message
}

/** A refused write in full: the headline and each problem it names, once. Queues are named, not coded. */
export function refusalText(err: unknown, lang: StaffLanguage, say: Say, queues: readonly BasicQueue[] = []): string {
  const problems = problemsNamed(err instanceof ApiError ? (err.problems ?? []) : [], queues)
  // BASIC_INVALID's headline asks to fix marked fields; the sheets mark none, the problems say it.
  const head = err instanceof ApiError && err.code === 'BASIC_INVALID' && problems.length > 0 ? '' : explain(err, lang, 'common.unreachable')
  const lines = [head, ...problems.map((p) => problemText(p, say, 'errors.'))]
  return [...new Set(lines.filter(Boolean))].join(' ')
}

/** Shown on the Basic screens while Advanced owns the configuration. */
export function ReadOnlyNote({ onAdvanced }: { onAdvanced: () => void }) {
  const { say } = useStaffText()
  return (
    <AskBar
      tone="default"
      role="note"
      title={say('catalogue.basic.readOnly')}
      actions={
        <Button variant="secondary" size="sm" onPress={onAdvanced}>
          {say('catalogue.basic.openAdvanced')}
        </Button>
      }
    />
  )
}

/** Basic is on, but the setup holds what Basic cannot show. */
export function NotBasicNote({ problems }: { problems: ProfileProblem[] }) {
  const { say } = useStaffText()
  const lines = [...new Set(problems.map((p) => problemText(p, say, 'errors.CONFIG_NOT_BASIC.')))]
  return (
    <Notice tone="default">
      <strong>{say('catalogue.basic.cannotShow')}</strong>
      <ul className="branch-list">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </Notice>
  )
}
